<template>
  <div>
    <b-overlay
      id="overlay-background"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <div>
        <FilterCard :applied-filter-count="appliedFilterCount">
          <b-row>
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group
                label="Date Range"
                label-for="v-ref-no"
              >
                <div class="position-relative">
                  <AdvancedDateFilter
                    :date-value="filters['osk20.created_at']"
                    :opens="'right'"
                    @update="updateDateRange('osk20.created_at', $event)"
                  />
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Apply and Clear Filters -->
          <b-row class="mt-1">
            <b-col>
              <filter-buttons
                :filter-data="filters"
                :apply-filters="applyFilters"
                :clear-filters="clearFilters"
              />
            </b-col>
          </b-row>
        </FilterCard>
      </div>
      <div>
        <b-card
          no-body
        >
          <div
            class="m-2"
          >
            <b-row>
              <b-col
                class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
              >
                <div>
                  <ReportTableButtons
                    :items="items"
                    :json_fieldz="json_fields"
                    :name="`${downloadReportName} - Page ${page}.xls`"
                    :bulkname="`${downloadReportName}.xls`"
                    :fetch="fetchReportNoPagination"
                    @refresh="fetchReport"
                  />
                </div>
              </b-col>
            </b-row>
          </div>
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
                @sort-changed="sortChanged"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(zero_return_orders_income)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #custom-foot>
              <b-tr class="bg-light total-row-footer">
                <b-th class="text-left">
                  Total:
                </b-th>
                <b-th class="text-right">
                  {{ totalZeroReturnOrderCount }}
                </b-th>
                <b-th class="text-right">
                  {{ __numberWithCommas(totalZeroReturnOrdersIncome, true) }}
                </b-th>
              </b-tr>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                md="3"
                class="d-flex align-items-center justify-content-sm-start"
              >
                <label class="mr-1">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block ml-1 width-125"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                md="9"
                class="d-flex align-items-center justify-content-sm-end"
              >
                <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :total-rows="(meta.total)? meta.total : 0"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
  BTr,
  BTh,
  VBTooltip,
} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import ReportTableButtons from '@/components/ReportTableUtils/ReportButtons/ReportTableButtons.vue'
import TableDataFetching from '@/components/TableDataFetching.vue'
import getMerchantList from '../ReturnOrderMerchant/MerchantListDropdown'

const ReportRepository = RepositoryFactory.get('report')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    FilterCard,
    vSelect,
    AdvancedDateFilter,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    BTr,
    BTh,
    FilterButtons,
    ReportTableButtons,
    TableDataFetching,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      merchantOptions: [],
      filters: {
        'osk20.created_at': {
          startDate: null,
          endDate: null,
        },
      },
      tableData: [],
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      dataLoading: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [
        {
          key: 'merchant_name',
          label: 'Merchant',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'zero_return_order_count',
          label: 'Return To Merchant Order Count',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'zero_return_orders_income',
          label: 'Lost Delivery Income',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
      ],
      json_fields: {
        Merchant: {
          field: 'merchant_name',
          callback: value => `"${value}"`,
        },
        'Return To Merchant Order Count': {
          field: 'zero_return_order_count',
        },
        'Lost Delivery Income': {
          field: 'zero_return_orders_income',
          callback: value => `"${value}"`,
        },
      },
      downloadReportName: 'Return-Free-Merchants-Report',
      items: [],
    }
  },
  computed: {
    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    totalZeroReturnOrderCount() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.zero_return_order_count) || 0), 0)
    },
    totalZeroReturnOrdersIncome() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.zero_return_orders_income) || 0), 0)
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchReport()
      },
    },
    deep: true,
    page() {
      this.fetchReport()
    },
    perPage() {
      this.fetchReport()
    },
  },
  mounted() {
    this.totalRows = this.items.length
    this.setMonthFilter()
    this.fetchReport()
    this.activeMerchants()
    this.updateFilterCount()
  },
  methods: {
    async fetchReport() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = await ReportRepository.getReturnFreeMerchantsReport(this.page, filterQuery, this.perPage)
        this.items = data.data || []
        this.meta = data.meta || {}
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchReportNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = await ReportRepository.getReturnFreeMerchantsReportNoPagination(filterQuery)
        return data.data || []
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    handleChangePage(page) {
      this.page = page
      this.fetchReport()
    },
    async activeMerchants() {
      this.merchantOptions = await getMerchantList()
    },
    setMonthFilter() {
      const currentMonth = this.monthFilter()
      this.updateDateRange('osk20.created_at', currentMonth)
    },
    applyFilters() {
      this.fetchReport()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        'osk20.created_at': {
          startDate: null,
          endDate: null,
        },
      }
      this.setMonthFilter()
      this.$nextTick(() => {
        this.fetchReport()
      })
      this.updateFilterCount()
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';

.total-row-footer th {
  font-size: 1rem;
}
</style>
